import React, { useCallback } from "react";
import LicensePlateState from "@app.automotus.io/components/common/LicensePlateState";
import { Formik, Form, FormikHelpers } from "formik";
import * as yup from "yup";
import { useMutation } from "@apollo/client";
import { REGISTER_VEHICLE, RegisterVehicleData, RegisterVehicleVars } from "common/graphql/mutations";
import { GET_CURRENTLY_REGISTERED_VEHICLES } from "common/graphql/queries";
import { StateAbbreviation, VehicleTypeValue } from "@app.automotus.io/components/common";
import { VehicleMake, VehicleModel } from "common/graphql/queries";
import {
  VehicleMakeDropdownField,
  VehicleModelDropdownField,
  VehicleTypeDropdownField,
  YearDropdownField,
  LicensePlateField,
} from "@app.automotus.io/components/forms/VehicleInfoFields";
import { AddVehicleFormContent } from "./AddVehicleFormContent";
import { useSnackPackContext, SNACKBAR_MSGS } from "@app.automotus.io/components/context/SnackPack";

export interface AddVehicleFormProps {
  /** Handler called when a vehicle is successfully added */
  onVehicleAdded: (vehicleId: string) => void;
  /** Handler called when the form is cancelled */
  onCancel: () => void;
}

interface AddVehicleFormValues {
  make: VehicleMake | null;
  model: VehicleModel | null;
  year: number | null;
  vehicleType: VehicleTypeValue | null;
  licensePlateState: StateAbbreviation | null;
  licensePlate: string;
}

const initialValues: AddVehicleFormValues = {
  make: VehicleMakeDropdownField.defaultInitialValue,
  model: VehicleModelDropdownField.defaultInitialValue,
  year: YearDropdownField.defaultInitialValue,
  vehicleType: VehicleTypeDropdownField.defaultInitialValue,
  licensePlateState: null,
  licensePlate: LicensePlateField.defaultInitialValue,
};

const validationSchema = yup.object({
  make: VehicleMakeDropdownField.validationSchema.required("Make is required"),
  model: VehicleModelDropdownField.validationSchema.required("Model is required"),
  year: YearDropdownField.validationSchema,
  vehicleType: VehicleTypeDropdownField.validationSchema,
  licensePlateState: yup.string().nullable().required("State is required"),
  licensePlate: LicensePlateField.validationSchema,
});

/**
 * Container component for adding a new vehicle.
 * Handles form state, validation, and the REGISTER_VEHICLE mutation.
 */
export const AddVehicleForm: React.FC<AddVehicleFormProps> = ({ onVehicleAdded, onCancel }) => {
  const [registerVehicle] = useMutation<RegisterVehicleData, RegisterVehicleVars>(REGISTER_VEHICLE, {
    refetchQueries: [{ query: GET_CURRENTLY_REGISTERED_VEHICLES, variables: { limit: 1000 } }],
  });
  const { publishSnackbarMessage } = useSnackPackContext();

  const handleSubmit = useCallback(
    async (values: AddVehicleFormValues, helpers: FormikHelpers<AddVehicleFormValues>) => {
      // These are validated as required by the schema, so they will always have values
      if (!values.licensePlateState || !values.vehicleType) {
        return;
      }

      try {
        const result = await registerVehicle({
          variables: {
            licensePlate: values.licensePlate,
            stateCode: values.licensePlateState,
            countryCode:
              LicensePlateState.FromAbbreviation(values.licensePlateState).countryCode === "CA" ? "CAN" : "USA",
            type: values.vehicleType,
            make: values.make?.name ?? null,
            model: values.model?.name ?? null,
            year: values.year,
          },
        });

        if (result.data?.registerVehicle.registeredVehicle) {
          const newVehicleId = result.data.registerVehicle.registeredVehicle.id;
          publishSnackbarMessage(SNACKBAR_MSGS.VEHICLE_ADDED_SUCCESS);
          helpers.resetForm();
          onVehicleAdded(newVehicleId);
        }
      } catch (error) {
        console.error("Error registering vehicle:", error);
        const errorMessage = error instanceof Error ? error.message : "";
        if (errorMessage.includes("registered_vehicle exists and is registered: true")) {
          publishSnackbarMessage(SNACKBAR_MSGS.VEHICLE_ALREADY_EXISTS);
        } else {
          publishSnackbarMessage(SNACKBAR_MSGS.VEHICLE_ADDED_FAILURE);
        }
        helpers.setStatus({ error: "Failed to register vehicle. Please try again." });
      }
    },
    [registerVehicle, onVehicleAdded, publishSnackbarMessage],
  );

  return (
    <Formik<AddVehicleFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, isSubmitting, isValid, dirty, submitForm }) => (
        <Form>
          <AddVehicleFormContent
            isSubmitting={isSubmitting}
            isValid={isValid && dirty}
            onSubmit={submitForm}
            onCancel={onCancel}
            makeValue={values.make}
            modelValue={values.model}
          />
        </Form>
      )}
    </Formik>
  );
};

export default AddVehicleForm;
